/**
 * The auto-bet rule's LIFE, as a pure step function (B32). The planner in
 * `birdieAutoBet.ts` answers "what would the rule put on this round"; this
 * module answers "is the rule still running, and has this round been answered
 * already" — one plan per race id, a count that moves only when bets really
 * went out, and the four ways it ends.
 *
 * Pure so the hook stays thin: it feeds the board's own signals in as events
 * (the round opening with its odds, the placement's outcome, the submission's
 * result, a hand on the board) and reads back the plan to place and the reason
 * it stopped, if it did. No clock — `arm` carries the moment, so the strip's
 * spin has a timestamp without this file ever asking for one.
 *
 * Identity is part of the contract: a step that changes nothing returns the
 * SAME state object, so a React consumer can skip the render. Every other
 * step returns a fresh one.
 */

import {
  planAutoBets,
  type AutoBetPlan,
  type AutoBetRule,
  type AutoBetSkip,
  type ZoneBounds,
  type ZoneMarkets
} from './birdieAutoBet'
import type { BirdieChipAction, ChipLimits, ChipOutcome } from './birdieChips'
import type { BirdieTileView } from './tileViewModel'

/**
 * Why the rule stopped. `player` is the toggle; `manual-edit` is the
 * platform's own rule that a hand on the board turns auto bet off, never both
 * at once; `balance` is the money running out, whether the planner or the
 * reducer saw it first; `finished` is the count reaching zero.
 */
export type AutoBetStopReason = 'player' | 'manual-edit' | 'balance' | 'finished'

export interface AutoBetMachineState {
  /** `null` = idle. */
  rule: AutoBetRule | null
  /** When the rule was armed, the caller's clock — the strip's spin anchors on it. */
  armedAtMs: number
  /** The race the rule has already answered; the same id is never planned twice. */
  plannedRaceId?: string
  /** Why the most recent round was skipped, for the pill; cleared by a placed round. */
  lastSkip?: AutoBetSkip
}

export const IDLE_AUTO_BET: AutoBetMachineState = { rule: null, armedAtMs: 0 }

export type AutoBetEvent =
  | { type: 'arm'; rule: AutoBetRule; nowMs: number }
  | { type: 'disarm' }
  | {
      type: 'roundOpen'
      raceId: string
      tiles: readonly BirdieTileView[]
      limits: ChipLimits
      marketIdByOption: ReadonlyMap<string, string>
    }
  /** The outcome of `applyAutoBetPlan` for the plan `roundOpen` handed back. */
  | { type: 'placed'; raceId: string; ok: boolean; skip?: AutoBetSkip }
  /** The close-time submission's verdict: did anything really go to the wire. */
  | { type: 'wireResult'; raceId: string; betsActuallySent: boolean }
  | { type: 'manualEdit' }

export interface AutoBetStep {
  state: AutoBetMachineState
  /** Set by `roundOpen` when the round was planned — skipped plans included, so the caller can say why. */
  plan?: AutoBetPlan
  stopped?: AutoBetStopReason
}

const unchanged = (state: AutoBetMachineState): AutoBetStep => ({ state })

const stopped = (reason: AutoBetStopReason, plan?: AutoBetPlan): AutoBetStep =>
  plan === undefined ? { state: IDLE_AUTO_BET, stopped: reason } : { state: IDLE_AUTO_BET, plan, stopped: reason }

/**
 * `arm` never carries a planned race: the hook replays `roundOpen` for the
 * round already open right after, so arming mid-window places at once — and
 * re-arming with a new rule answers the open round again under the new rule.
 *
 * A balance skip disarms wherever it is seen. The planner sees it first when
 * the whole plan is more than the balance; the reducer sees it when the
 * balance moved between the plan and the placement. Either way the rule
 * cannot do its job, and a rule that silently waits for money is a bet the
 * player did not expect later.
 */
export function autoBetStep(state: AutoBetMachineState, event: AutoBetEvent): AutoBetStep {
  switch (event.type) {
    case 'arm':
      return { state: { rule: event.rule, armedAtMs: event.nowMs } }

    case 'disarm':
      return state.rule === null ? unchanged(state) : stopped('player')

    case 'roundOpen': {
      if (state.rule === null || event.raceId === state.plannedRaceId) return unchanged(state)
      const plan = planAutoBets({
        tiles: event.tiles,
        rule: state.rule,
        limits: event.limits,
        marketIdByOption: event.marketIdByOption
      })
      if (plan.skip === 'balance') return stopped('balance', plan)
      return { state: { ...state, plannedRaceId: event.raceId, lastSkip: plan.skip }, plan }
    }

    case 'placed': {
      if (state.rule === null) return unchanged(state)
      const skip = event.ok ? undefined : event.skip
      if (skip === 'balance') return stopped('balance')
      if (skip === state.lastSkip) return unchanged(state)
      return { state: { ...state, lastSkip: skip } }
    }

    /**
     * The count is spent by bets that went out, not by rounds that opened: a
     * held submission (nothing on the wire) leaves the count where it was, and
     * a result for a race this rule never planned is not its round to count.
     * At zero the round's bets are already on the wire, so finishing here
     * cancels nothing.
     */
    case 'wireResult': {
      const rule = state.rule
      if (rule === null) return unchanged(state)
      if (rule.roundsLeft === null || !event.betsActuallySent || event.raceId !== state.plannedRaceId) {
        return unchanged(state)
      }
      const roundsLeft = rule.roundsLeft - 1
      if (roundsLeft <= 0) return stopped('finished')
      return { state: { ...state, rule: { ...rule, roundsLeft } } }
    }

    /** The chips stay: the machine does not own them, the player just took them over. */
    case 'manualEdit':
      return state.rule === null ? unchanged(state) : stopped('manual-edit')
  }
}

/**
 * Whether the board belongs to this step. The board is the rule's while it is
 * armed, so every round the rule ANSWERS starts from an empty one — placed,
 * skipped, or stopped on for the balance alike. Without that, chips carried
 * forward from a held or aborted submission (`race_ready` → `roundReset` with
 * `keep: true`) would go to the wire on a round the pill says was skipped, on
 * options this round's prices put outside the zone.
 *
 * A round the machine has already answered says no, so the planning effect —
 * which re-runs on every balance tick — never wipes a board mid-window.
 */
export const clearsBoard = (step: AutoBetStep, event: AutoBetEvent): boolean =>
  event.type === 'roundOpen' && step.plan !== undefined

const skipForNotice = (notice: NonNullable<ChipOutcome['notice']>): AutoBetSkip =>
  notice === 'insufficient-balance' || notice === 'balance-loading' ? 'balance' : 'clamped'

/**
 * Put a plan on the board through the ordinary `place` actions. The board is
 * the rule's while it is armed, so it is cleared first; a plan the planner
 * already skipped never touches the board at all.
 *
 * The reducer is the last word: its limits can differ from the planner's if
 * the balance or the caps moved in between, and the first placement it refuses
 * ends the round — the board is cleared again so no partial set stands, and
 * the notice is read back as the skip the pill can name. A refusal that is not
 * about money is reported as a clamp, the planner's own word for a stake the
 * board would not take whole.
 */
export function applyAutoBetPlan(
  plan: AutoBetPlan,
  dispatch: (action: BirdieChipAction) => ChipOutcome
): { ok: boolean; skip?: AutoBetSkip } {
  if (plan.skip !== undefined) return { ok: false, skip: plan.skip }
  dispatch({ type: 'clear' })
  for (const placement of plan.placements) {
    const outcome = dispatch({
      type: 'place',
      optionId: placement.optionId,
      marketId: placement.marketId,
      amount: placement.amount
    })
    if (outcome.notice !== undefined) {
      dispatch({ type: 'clear' })
      return { ok: false, skip: skipForNotice(outcome.notice) }
    }
  }
  return { ok: true }
}

/** What the pill says while armed: the zone's bounds, the markets, the rounds left. Null when idle. */
export function autoBetSummary(
  state: AutoBetMachineState
): { zone: ZoneBounds; markets: ZoneMarkets; roundsLeft: number | null } | null {
  if (state.rule === null) return null
  return {
    zone: { min: state.rule.zone.min, max: state.rule.zone.max },
    markets: state.rule.markets,
    roundsLeft: state.rule.roundsLeft
  }
}
